import {
  ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg, StandardActionToastMessages
} from '~commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { SearchSuggestionsService } from '~content/services/SearchSuggestionsService';
import { toQPromise } from '~commonInfra/utils/QUtils';

import {
  Recurrence, RecurrenceDetails, SelfServiceSchedulePaneProps, StaticInitializers, TimeZoneDetails
} from '@tableau/self-service-schedules';
import {
  DailyRecurrenceDetails, HourlyRecurrenceDetails, MonthlyRecurrenceDetails, RecurrenceMode,
  RecurrenceType, WeeklyRecurrenceDetails
} from '@tableau/self-service-schedules/lib/src/components/Recurrence';
import {
  SelfServiceSchedulePaneType
} from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { SiteUrls } from '@tableau/shared-urls';

import { getAppStore } from '../../app/AppStore';
import { SessionServerSelectors } from '../../app/selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { AccelerationTasks } from '../../dataAcceleration/resources/AccelerationTasks';
import { Subscriptions } from '../../dda/resources/Subscriptions';
import { ExtractTasks } from '../../extract/resources/ExtractTasks';
import { RunFlowTasks } from '../../flow/resources/RunFlowTasks';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import {
  SchedulePickerActionBody, SchedulePickerProps, SchedulePickerState, SubscriptionFrequency
} from '../../schedule/components/SchedulePickerActionBody';
import {
  SelfServiceScheduleDialogBody
} from '../../schedule/components/SelfServiceScheduleActionDialogWrapper';
import {
  DefinedBy, ISchedule, ISelfServiceScheduleDetails, ScheduleActions
} from '../../schedule/types/ISchedule';
import {
  getSessionManagementServiceInstance
} from '../../sessionManagement/SessionManagementService';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import {
  CommonErrorCode, DataAccelerationErrorCode, SchedulingErrorCode, SubscriptionErrorCode
} from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { ScheduleContentType } from '../models/ContentTypes';
import { IAccelerationTaskInfo } from '../models/IAccelerationTaskInfo';
import { IExtractTaskInfo } from '../models/IExtractTaskInfo';
import { ISubscriptionInfo, SubscriptionTargetTypes } from '../models/ISubscriptionInfo';
import { ITypeaheadSource } from '../models/ITypeaheadSource';
import { INamedItemInfo, IScheduledItemInfo } from '../models/resourceInfoParts';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { Set } from '../models/Set';
import { SchedulesService } from '../resources/Schedules';
import { ScheduleDetails } from '../ServerApi/types/ScheduleDetails';
import {
  scheduledActionEnum, scheduledTypeEnum, SelfServiceSchedule
} from '../ServerApi/types/SelfServiceSchedule';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ToasterService, ToastType } from './ToasterService';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { SubscriptionSchedulePickerActionBody, SubscriptionSchedulePickerProps } from '~src/schedule/components/SubscriptionSchedulePickerActionBody';

interface IChangeScheduleActionOptions<T> {
  notificationTranslationFns: StandardActionToastMessages;
  scheduledActionType: string;
  descriptionTranslateKey: string;
  descriptionTranslateValues: _.Dictionary<any>;
}

interface IChangeAdminScheduleActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (ids: string[], scheduleId: string) => ng.IPromise<IResult>;
}

interface IChangeSubscriptionsFrequencyOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: (schedule: ISchedule) => (errorItem: IErrorItem<ISubscriptionInfo>) => IErrorDetail;
  action: (ids: string[], scheduleId: string, subscribeOnExtractRefresh: boolean) =>
    ng.IPromise<IResult>;
}

interface IChangeSelfServiceSubscriptionsFrequencyOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: () => (errorItem: IErrorItem<ISubscriptionInfo>) => IErrorDetail;
  action: (ids: string[], schedule: SelfServiceSchedule, subscribeOnExtractRefresh: boolean) =>
    ng.IPromise<IResult>;
}

interface IChangeSelfServiceScheduleActionOptions<T> extends IChangeScheduleActionOptions<T> {
  errorDetailFn: () => (errorItem: IErrorItem<T>) => IErrorDetail;
  action: (ids: string[], schedule: SelfServiceSchedule) => ng.IPromise<IResult>;
}

export enum SubscriptionsFrequency {
  subscribeOnSelectedSchedule = 1 << 0,
  subscribeOnExtractRefresh = 1 << 1
}

export interface IChangeScheduleScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule;
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule;
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule;
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export interface IChangeSubscriptionsFrequencyScope extends ng.IScope {
  scheduleSource: ITypeaheadSource;
  itemTextFn?: (schedule: ISchedule) => string;
  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  filterList: {
    model: ISchedule;
  };
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}


export interface IChangeSelfServiceScheduleScope extends ng.IScope {
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;

  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
}

export interface IChangeSelfServiceSubscriptionsFrequencyScope extends ng.IScope {
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;

  displayScheduleDescriptionAsName: boolean;
  descriptionTranslateKey: string;
  descriptionTranslateValues?: _.Dictionary<any>;
  frequencyOptions: {
    'id': SubscriptionsFrequency; 'label': string;
  }[];
  selected: {
    id: SubscriptionsFrequency;
  };
}

export namespace ChangeScheduleActionService {
  // Generic logic
  export function createDialogScope<T>(parentScope: ng.IScope,
    items: Set<T>,
    options: IChangeScheduleActionOptions<T>): IChangeScheduleScope {

    const scope = <IChangeScheduleScope>parentScope.$new();

    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    scope.scheduleSource = SearchSuggestionsService.schedulesSource(options.scheduledActionType);
    scope.filterList = {
      model: null
    };

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = IScheduleInfo.getScheduleDescription(schedule);
      if (!schedule.parallel) {
        return Messages.serialScheduleDescription({
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: Messages.serialScheduleDescriptionSuffix()
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName();

    return scope;
  }

  export function createChangeSubscriptionsFrequencyScope(parentScope: ng.IScope,
    items: Set<ISubscriptionInfo>,
    options: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo>): IChangeSubscriptionsFrequencyScope {

    const scope = <IChangeSubscriptionsFrequencyScope>parentScope.$new();

    const subscription = items.first();
    const initialOnExtractRefresh: boolean = subscription.refreshExtractTriggered;
    const initialSelectedId = (initialOnExtractRefresh ? SubscriptionsFrequency.subscribeOnExtractRefresh : SubscriptionsFrequency.subscribeOnSelectedSchedule);

    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    scope.scheduleSource = SearchSuggestionsService.schedulesSource(options.scheduledActionType);
    scope.filterList = {
      model: null
    };

    scope.itemTextFn = (schedule: ISchedule) => {
      const description = IScheduleInfo.getScheduleDescription(schedule);
      if (!schedule.parallel) {
        return Messages.serialScheduleDescription({
          SERIAL_DESCRIPTION: description,
          SERIAL_DESCRIPTION_SUFFIX: Messages.serialScheduleDescriptionSuffix()
        });
      }
      return description;
    };

    scope.displayScheduleDescriptionAsName = getSessionManagementServiceInstance().displayScheduleDescriptionAsName();

    scope.frequencyOptions = [
      { 'id': SubscriptionsFrequency.subscribeOnSelectedSchedule, 'label': Messages.changeSubscriptionsFrequencyOptionOnSelectedSchedule() },
      { 'id': SubscriptionsFrequency.subscribeOnExtractRefresh, 'label': Messages.changeSubscriptionsFrequencyOptionOnExtractRefresh() }
    ];
    scope.selected = {
      'id': initialSelectedId
    };

    return scope;
  }

  export function getSelfServiceSchedulePaneProps(scheduleDetails: ISelfServiceScheduleDetails,
    timeZonesEnabled: boolean, timeZoneDisplayName: string, recurrenceType: string,
    type: SelfServiceSchedulePaneType): SelfServiceSchedulePaneProps {
    const details = createRecurrence(scheduleDetails, recurrenceType);
    const recurrence = {
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: recurrenceType,
      recurrenceDetails: details
    } as Recurrence;

    const sessionManagementService = getSessionManagementServiceInstance();
    const userAccountSettingsLink = new SiteUrls({
      siteUrlName: sessionManagementService.sessionSiteUrlName(),
      queryString: window.location.search
    }).userSettingsUrl(sessionManagementService.sessionUserInfo().username, sessionManagementService.sessionUserInfo().domainName);

    const timeZoneDetails: TimeZoneDetails = {
      timeZonesEnabled: timeZonesEnabled,
      timeZoneDisplayName: timeZoneDisplayName,
      linkToUserSettings: userAccountSettingsLink
    };

    const selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps = {
      recurrenceModel: recurrence,
      timeZoneDetails: timeZoneDetails,
      type: type,
      recurrenceModelChangedCallback: (model) => { }
    };

    return selfServiceSchedulePaneProps;
  }

  export function setupSelfServiceScheduleDialogScope<T extends IChangeSelfServiceScheduleScope>(scope: T,
    scheduleDetails: ISelfServiceScheduleDetails, timeZonesEnabled: boolean,
    timeZoneDisplayName: string, recurrenceType: string, type: SelfServiceSchedulePaneType) {

    scope.selfServiceSchedulePaneProps = getSelfServiceSchedulePaneProps(
      scheduleDetails, timeZonesEnabled, timeZoneDisplayName, recurrenceType, type);
    scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback = (newRecurrence: Recurrence) => {
      scope.$apply(() => {
        scope.selfServiceSchedulePaneProps = {
          recurrenceModel: newRecurrence,
          timeZoneDetails: scope.selfServiceSchedulePaneProps.timeZoneDetails,
          recurrenceModelChangedCallback: scope.selfServiceSchedulePaneProps.recurrenceModelChangedCallback,
          type: type,
        };
      });
    };
  }

  function createChangeSelfServiceSubscriptionsFrequencyScope<T extends IScheduledItemInfo>(parentScope: ng.IScope,
    subscription: ISubscriptionInfo,
    options: IChangeSelfServiceSubscriptionsFrequencyOptions<T>):
    IChangeSelfServiceSubscriptionsFrequencyScope {

    const scope = <IChangeSelfServiceSubscriptionsFrequencyScope>parentScope.$new();
    scope.descriptionTranslateKey = options.descriptionTranslateKey;
    scope.descriptionTranslateValues = options.descriptionTranslateValues;

    const schedule = subscription.schedule;
    const scheduleDetails = <ISelfServiceScheduleDetails>schedule.scheduleDetails;
    const userTimeZoneDisplay = getSessionManagementServiceInstance().userTimeZone().displayName;
    const initialOnExtractRefresh: boolean = subscription.refreshExtractTriggered;
    const initialSelectedId = (initialOnExtractRefresh ? SubscriptionsFrequency.subscribeOnExtractRefresh : SubscriptionsFrequency.subscribeOnSelectedSchedule);

    scope.frequencyOptions = [
      { 'id': SubscriptionsFrequency.subscribeOnSelectedSchedule, 'label': Messages.changeSubscriptionsFrequencyOptionOnSelectedSchedule() },
      { 'id': SubscriptionsFrequency.subscribeOnExtractRefresh, 'label': Messages.changeSubscriptionsFrequencyOptionOnExtractRefresh() }
    ];
    scope.selected = {
      'id': initialSelectedId
    };

    setupSelfServiceScheduleDialogScope(scope, scheduleDetails, FeatureFlags.userTimeZoneEnabled,
      userTimeZoneDisplay, schedule.scheduleType, SelfServiceSchedulePaneType.Default);
    return scope;
  }

  function createRecurrence(scheduleDetails: ISelfServiceScheduleDetails, recurrenceType: string): RecurrenceDetails {
    switch (recurrenceType) {
      case RecurrenceType.Hourly:
        return {
          startTime: scheduleDetails.startTime,
          endTime: scheduleDetails.endTime,
          recurrenceMinutes: scheduleDetails.recurrenceMinutes,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as HourlyRecurrenceDetails;
      case RecurrenceType.Daily:
        return {
          startTime: scheduleDetails.startTime,
          endTime: scheduleDetails.endTime,
          recurrenceMinutes: scheduleDetails.recurrenceMinutes,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as DailyRecurrenceDetails;
      case RecurrenceType.Weekly:
        return {
          startTime: scheduleDetails.startTime,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as WeeklyRecurrenceDetails;
      case RecurrenceType.Monthly:
        return {
          startTime: scheduleDetails.startTime,
          recurrenceDayOfMonth: scheduleDetails.recurrenceDayOfMonth,
          recurrenceDaysOfWeek: scheduleDetails.recurrenceDaysOfWeek
        } as MonthlyRecurrenceDetails;
      default:
        return StaticInitializers.defaultHourly();
    }
  }

  function mixedScheduleTypesUnsupported(): ng.IPromise<IResult> {
    ToasterService.instance.pop({ type: ToastType.Error, text: Messages.mixedScheduleTypesUnsupported() });
    return null;
  }

  function executeSelfServiceSchedule<T extends IScheduledItemInfo>(
    items: Set<T>,
    context: IActionContext,
    options: IChangeSelfServiceScheduleActionOptions<T>
  ): ng.IPromise<IResult> {
    const schedule = items.first().schedule;
    const timeZonesEnabled = FeatureFlags.userTimeZoneEnabled;
    const timeZoneDisplayName = getSessionManagementServiceInstance().userTimeZone().displayName;
    const sssProps = getSelfServiceSchedulePaneProps(schedule.scheduleDetails, timeZonesEnabled, timeZoneDisplayName, schedule.scheduleType, SelfServiceSchedulePaneType.Default);
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SelfServiceScheduleDialogBody,
      initialBodyProps: sssProps,
      initialDialogState: sssProps.recurrenceModel,
      actionName: Messages.done(),
      dialogWidth: TabDialogWidth.width500px,
      dismissDialogOnActionFailed: true,
      action: (bodyProps: SelfServiceSchedulePaneProps, recurrence: Recurrence) => {
        const schedule: SelfServiceSchedule = createSelfServiceScheduleFromRecurrenceModel(recurrence);
        return options.action(items.keys(), schedule);
      }
    });
    return modalInstance.result;
  }

  function executeChangeSelfServiceSubscriptionsFrequency(
    subscriptions: Set<ISubscriptionInfo>,
    context: IActionContext,
    options: IChangeSelfServiceSubscriptionsFrequencyOptions<ISubscriptionInfo>
  ): ng.IPromise<IResult> {

    const subscription = subscriptions.first();
    const scope = createChangeSelfServiceSubscriptionsFrequencyScope(context.scope, subscription, options);

    const dialogInstance = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog').open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeSelfServiceSubscriptionFrequency.html',
      title: Messages.changeSubscriptionsFrequency(),
      actionName: Messages.done(),
      size: '500px',
      position: {
        reserveHeight: 800
      },
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        const schedule: SelfServiceSchedule = createSelfServiceScheduleFromRecurrenceModel(
          scope.selfServiceSchedulePaneProps.recurrenceModel);

        const selectedOnExtractRefresh: boolean = !!(scope.selected.id & SubscriptionsFrequency.subscribeOnExtractRefresh);
        // If we have chosen to subscribe based on an extract refresh, make a call to setSubscriptionsSchedule with following parameters:
        // subscription id, a Self Service Schedule based on the default Recurrence Model, and the value of subscribeOnExtractRefresh
        if (selectedOnExtractRefresh) {
          return Subscriptions.changeSelfServiceSubscriptionsFrequency(subscriptions.map(sub => sub.id), schedule, selectedOnExtractRefresh).then((
            result: IResult) => {
            if (!result.errors || !result.errors.length) {
              subscription.refreshExtractTriggered = selectedOnExtractRefresh;
            }
            return result;
          });
        } else {
          return options.action(subscriptions.keys(), schedule, selectedOnExtractRefresh);
        }
      }
    });

    dialogInstance.result.then(result =>
      ActionHelpers.displayStandardToastsForResult(
        subscriptions,
        result,
        options.notificationTranslationFns,
        (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn()(errorItem)
      )
    );

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  function executeReact<T>(
    items: Set<T>,
    options: IChangeAdminScheduleActionOptions<T>,
  ): ng.IPromise<IResult> {
    const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());
    const scheduledActionType = options.scheduledActionType;

    let description = Messages.errorUnknown();
    try {
      const castKey = options.descriptionTranslateKey as keyof typeof Messages;
      const castValues = options.descriptionTranslateValues as any;
      description = Messages[castKey](castValues);
    } catch (e) {
      // Do nothing
    }
    const props: SchedulePickerProps = {
      scheduledActionType, useDescriptionAsName, getSchedules: getSchedules(scheduledActionType),
      description: description,
    };
    const errorReportingAction = (props: SchedulePickerProps, state: SchedulePickerState) => options
      .action(items.keys(), state.schedule.id)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          items,
          result,
          options.notificationTranslationFns,
          (errorItem: IErrorItem<T>) => options.errorDetailFn(state.schedule)(errorItem)
        ));
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SchedulePickerActionBody,
      initialBodyProps: props,
      initialDialogState: undefined,
      actionName: Messages.changeFrequencyConfirm({ COUNT: items.size(), COUNT_LOC: L10n.localizedCount(items.size()) }),
      action: errorReportingAction,
      actionDisabled: (props, schedule) => schedule === undefined,
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width500px,
    });

    return modalInstance.result;
  }

  function execute<T>(
    items: Set<T>,
    context: IActionContext,
    options: IChangeAdminScheduleActionOptions<T>
  ): ng.IPromise<IResult> {
    if (FeatureFlags.schedulesARMEnabled) {
      return executeReact(items, options);
    }

    const scope = createDialogScope(context.scope, items, options);

    const getSchedule = () => scope.filterList.model;

    const dialogInstance = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog').open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeSchedule.html',
      title: Messages.changeFrequency(),
      actionName: Messages.changeFrequencyConfirm({ COUNT: items.size(), COUNT_LOC: L10n.localizedCount(items.size()) }),
      action: () => options.action(items.keys(), getSchedule().id),
      confirmDisabled: () => !getSchedule()
    });

    dialogInstance.result.then(result =>
      ActionHelpers.displayStandardToastsForResult(
        items,
        result,
        options.notificationTranslationFns,
        (errorItem: IErrorItem<T>) => options.errorDetailFn(getSchedule())(errorItem)
      )
    );

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  export function getSchedules(scheduledActionType: string) {
    // Filtering happens client side so we ignore the search string
    const getSchedulesMethod = (searchText: string) => {
      let query = SchedulesService.all().withOrder(ScheduleContentType.defaultOrder);
      if (scheduledActionType) {
        query = query.addFilter(SchedulesService.filterByActionType(scheduledActionType));
      }
      return query;
    };
    return getSchedulesMethod;
  }

  function executeReactForSubscription(
    subscriptions: Set<ISubscriptionInfo>,
    options: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo>,
  ): ng.IPromise<IResult> {
    const useDescriptionAsName = SessionServerSelectors.displayScheduleDescriptionAsName(getAppStore().getState());
    const scheduledActionType = options.scheduledActionType;
    const subscription = subscriptions.first();
    const getSchedulesMethod = getSchedules(scheduledActionType);

    let description = Messages.errorUnknown();
    try {
      const castKey = options.descriptionTranslateKey as keyof typeof Messages;
      const castValues = options.descriptionTranslateValues as any;
      description = Messages[castKey](castValues);
    } catch (e) {
      // Do nothing
    }
    const props: SubscriptionSchedulePickerProps = {
      scheduledActionType, useDescriptionAsName, getSchedules: getSchedulesMethod,
      description: description,
      selectedSubscriptionFrequency: subscription.refreshExtractTriggered ? SubscriptionFrequency.onExtractRefresh : SubscriptionFrequency.onSelectedSchedule
    };
    const errorReportingAction = (props: SchedulePickerProps, state: SchedulePickerState) => {
      if (state.selectedScheduleOption === SubscriptionFrequency.onExtractRefresh) {
        return Subscriptions.changeFrequency(subscriptions.map(sub => sub.id), subscription.schedule.id, true)
          .then(result => {
            if (!result.errors || !result.errors.length) {
              //no error mark it as extract refresh
              subscription.refreshExtractTriggered = true;
            }
            return ActionHelpers.displayStandardToastsForResult(
              subscriptions,
              result,
              options.notificationTranslationFns,
              (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn(state.schedule)(errorItem)
            );
          });
      } else {
        return Subscriptions.changeFrequency(subscriptions.keys(), state.schedule.id, false)
          .then(result =>
            ActionHelpers.displayStandardToastsForResult(
              subscriptions,
              result,
              options.notificationTranslationFns,
              (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn(state.schedule)(errorItem)
            ));
      }
    };
    const modalInstance = openReactActionDialog({
      title: Messages.changeFrequency(),
      bodyComponent: SubscriptionSchedulePickerActionBody,
      initialBodyProps: props,
      initialDialogState: undefined,
      actionName: Messages.changeSubscriptionsFrequencyConfirm({ COUNT: subscriptions.size(), COUNT_LOC: L10n.localizedCount(subscriptions.size()) }),
      action: errorReportingAction,
      actionDisabled: (props, state) => {
        const isOnExtractRefresh = state?.selectedScheduleOption === SubscriptionFrequency.onExtractRefresh;
        const hasDefinedSchedule = state?.schedule !== undefined;
        return !(isOnExtractRefresh || hasDefinedSchedule);
      },
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width500px,
    });

    return modalInstance.result;
  }

  function executeChangeSubscriptionsFrequency(
    subscriptions: Set<ISubscriptionInfo>,
    context: IActionContext,
    options: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo>
  ): ng.IPromise<IResult> {
    if (FeatureFlags.schedulesARMEnabled) {
      return executeReactForSubscription(subscriptions, options);
    }
    const scope = createChangeSubscriptionsFrequencyScope(context.scope, subscriptions, options);

    const getSchedule = () => scope.filterList.model;

    const subscription = subscriptions.first();

    const dialogInstance = ServiceLocator.get<ConfirmActionDialog>('ConfirmActionDialog').open({
      scope: scope,
      bodyTemplateUrl: 'confirmActionDialogChangeFrequency.html',
      title: Messages.changeSubscriptionsFrequency(),
      actionName: Messages.changeSubscriptionsFrequencyConfirm({ COUNT: subscriptions.size(), COUNT_LOC: L10n.localizedCount(subscriptions.size()) }),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        const selectedOnExtractRefresh: boolean = !!(scope.selected.id & SubscriptionsFrequency.subscribeOnExtractRefresh);
        // If we have chosen to subscribe based on an extract refresh, make a call to setSubscriptionsSchedule with following parameters:
        // subscription id, subscriptions *current* schedule id (to handle rollback scenario), and the value of subscribeOnExtractRefresh
        if (selectedOnExtractRefresh) {
          return Subscriptions.changeFrequency(subscriptions.map(sub => sub.id), subscription.schedule.id, selectedOnExtractRefresh).then((
            result: IResult) => {
            if (!result.errors || !result.errors.length) {
              subscription.refreshExtractTriggered = selectedOnExtractRefresh;
            }
            return result;
          });
        } else {
          return options.action(subscriptions.keys(), getSchedule().id, selectedOnExtractRefresh);
        }
      },
      // The confirm button should only be disabled if we have chosen to subscribe on a selected schedule, and we haven't yet
      // chosen a schedule
      confirmDisabled: () => !(scope.selected.id & SubscriptionsFrequency.subscribeOnExtractRefresh) && !getSchedule()
    });

    dialogInstance.result.then(result =>
      ActionHelpers.displayStandardToastsForResult(
        subscriptions,
        result,
        options.notificationTranslationFns,
        (errorItem: IErrorItem<ISubscriptionInfo>) => options.errorDetailFn(getSchedule())(errorItem)
      )
    );

    dialogInstance.result.finally(() => scope.$destroy());
    return dialogInstance.result;
  }

  function setScheduleErrorMessageFor(errorItem: IErrorItem<IScheduledItemInfo>, schedule: ISchedule) {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingScheduleNotFound:
        return Messages.scheduleNotFound({ SCHEDULE: schedule.name });
    }
    return null;
  }

  export function createSelfServiceScheduleFromRecurrenceModel(recurrenceModel: Recurrence): SelfServiceSchedule {

    const selfServiceSchedule: SelfServiceSchedule = {
      scheduledAction: <scheduledActionEnum>'Subscription',
      scheduleType: <scheduledTypeEnum>recurrenceModel.recurrenceType,
      scheduleDetails: recurrenceModel.recurrenceDetails as ScheduleDetails
    };

    return selfServiceSchedule;
  }

  function isMixedSchedulesTypes(subscriptions: Set<ISubscriptionInfo>): boolean {
    for (const abc of subscriptions.toArray()) {
      if (abc.schedule.definedBy !== subscriptions.first().schedule.definedBy) {
        return true;
      }
    }
    return false;
  }

  // Subscription specific logic

  export function forSubscriptions(subscriptions: Set<ISubscriptionInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (subscriptions.size() === 1) {
      const sub = subscriptions.first();
      descriptionTranslateValues = { TARGET_NAME: L10n.elideIfLong(sub.targetName) };
      if (sub.targetType === SubscriptionTargetTypes.view) {
        descriptionTranslateKey = 'changeScheduleSubscriptionViewPrompt';
      } else if (sub.targetType === SubscriptionTargetTypes.workbook) {
        descriptionTranslateKey = 'changeScheduleSubscriptionWorkbookPrompt';
      }
    } else {
      descriptionTranslateKey = 'changeScheduleSubscriptionsPrompt';
      descriptionTranslateValues = { COUNT: subscriptions.size() };
    }

    const options: IChangeScheduleActionOptions<ISubscriptionInfo> = {
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeScheduleSubscriptionsSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleSubscriptionsError),
      },
      scheduledActionType: ScheduleActions.Subscription,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    if (isMixedSchedulesTypes(subscriptions)) {
      return mixedScheduleTypesUnsupported();
    }

    if (FeatureFlags.selfServiceSchedulesEnabled && subscriptions.first().schedule.definedBy === DefinedBy.SELF_SERVICE) {
      const SSSOptions: IChangeSelfServiceScheduleActionOptions<ISubscriptionInfo> = {
        action: (ids, schedule) => Subscriptions.setSelfServiceSchedule(ids, schedule),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: () => subscriptionErrorDetailFactorySSS(),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues,
      };
      return executeSelfServiceSchedule(subscriptions, context, SSSOptions);
    } else {
      const adminOptions: IChangeAdminScheduleActionOptions<ISubscriptionInfo> = {
        action: (ids, scheduleId) => Subscriptions.setSchedule(ids, scheduleId),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: (schedule) => subscriptionErrorDetailFactory(schedule),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues
      };
      return execute(subscriptions, context, adminOptions);
    }
  }

  export function changeSubscriptionsFrequency(subscriptions: Set<ISubscriptionInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (subscriptions.size() === 1) {
      const sub = subscriptions.first();
      descriptionTranslateValues = { TARGET_NAME: L10n.elideIfLong(sub.targetName) };
      if (sub.targetType === SubscriptionTargetTypes.view) {
        descriptionTranslateKey = 'changeScheduleSubscriptionViewPrompt';
      } else if (sub.targetType === SubscriptionTargetTypes.workbook) {
        descriptionTranslateKey = 'changeScheduleSubscriptionWorkbookPrompt';
      }
    } else {
      descriptionTranslateKey = 'changeScheduleSubscriptionsPrompt';
      descriptionTranslateValues = { COUNT: subscriptions.size() };
    }

    const options: IChangeScheduleActionOptions<ISubscriptionInfo> = {
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeSubscriptionsFrequencySuccess),
        error: standardActionErrorMsg(Messages.changeSubscriptionsFrequencyFailure),
      },
      scheduledActionType: ScheduleActions.Subscription,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    if (isMixedSchedulesTypes(subscriptions)) {
      return mixedScheduleTypesUnsupported();
    }

    if (FeatureFlags.selfServiceSchedulesEnabled && subscriptions.first().schedule.definedBy === DefinedBy.SELF_SERVICE) {
      const SSSOptions: IChangeSelfServiceSubscriptionsFrequencyOptions<ISubscriptionInfo> = {
        action: (ids, schedule, subscribeOnExtractRefresh) =>
          toQPromise(Subscriptions.changeSelfServiceSubscriptionsFrequency(ids, schedule, subscribeOnExtractRefresh)),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: () => subscriptionErrorDetailFactorySSS(),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues,
      };
      return executeChangeSelfServiceSubscriptionsFrequency(subscriptions, context, SSSOptions);
    } else {
      const adminOptions: IChangeSubscriptionsFrequencyOptions<ISubscriptionInfo> = {
        action: (ids, scheduleId, subscribeOnExtractRefresh) =>
          toQPromise(Subscriptions.changeFrequency(ids, scheduleId, subscribeOnExtractRefresh)),
        notificationTranslationFns: options.notificationTranslationFns,
        errorDetailFn: (schedule) => subscriptionErrorDetailFactory(schedule),
        scheduledActionType: options.scheduledActionType,
        descriptionTranslateKey: options.descriptionTranslateKey,
        descriptionTranslateValues: options.descriptionTranslateValues
      };
      return executeChangeSubscriptionsFrequency(subscriptions, context, adminOptions);
    }
  }

  function subscriptionErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<ISubscriptionInfo>): IErrorDetail => {
      return {
        itemName: errorItem.item.subject,
        errorMessage: subscriptionErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function subscriptionErrorDetailFactorySSS() {
    return (errorItem: IErrorItem<ISubscriptionInfo>): IErrorDetail => {
      return {
        itemName: errorItem.item.subject,
        errorMessage: Messages.changeSubscriptionScheduleFailure()
      };
    };
  }

  function subscriptionErrorMessageFor(errorItem: IErrorItem<ISubscriptionInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case CommonErrorCode.SubscriptionNotFound:
        return Messages.subscriptionNotFound({ SUBSCRIPTION_SUBJECT: errorItem.item.subject });
      case SubscriptionErrorCode.SubscriptionFrequencyChangeToERSErrorCode:
        return Messages.subscriptionChangeToERSFailed();
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }

  // Extract Tasks specific logic

  export function forExtractTasks(extractTasks: Set<IExtractTaskInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (extractTasks.size() === 1) {
      const task = extractTasks.first();
      if (task.workbook) {
        descriptionTranslateKey = 'changeScheduleExtractTasksWorkbookPrompt';
        descriptionTranslateValues = { PARENT_NAME: L10n.elideIfLong(task.workbook.name) };
      } else if (task.datasource) {
        descriptionTranslateKey = 'changeScheduleExtractTasksDatasourcePrompt';
        descriptionTranslateValues = { PARENT_NAME: L10n.elideIfLong(task.datasource.name) };
      }
    } else {
      descriptionTranslateKey = 'changeScheduleExtractTasksPrompt';
      descriptionTranslateValues = { COUNT: extractTasks.size() };
    }

    const options: IChangeAdminScheduleActionOptions<IExtractTaskInfo> = {
      action: (ids, scheduleId) => ExtractTasks.setSchedule(ids, scheduleId),
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeScheduleExtractTasksSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleExtractTasksError),
      },
      errorDetailFn: (schedule) => extractTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Extract,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return execute(extractTasks, context, options);
  }

  function extractTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IExtractTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.datasource || errorItem.item.workbook;

      return {
        itemName: namedItem.name,
        errorMessage: extractTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function extractTaskErrorMessageFor(errorItem: IErrorItem<IExtractTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        if (errorItem.item.workbook)
          return Messages.extractTaskNotFoundForWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(errorItem.item.workbook.name) });
        if (errorItem.item.datasource)
          return Messages.extractTaskNotFoundForDatasource({ DATASOURCE_NAME: L10n.elideIfLong(errorItem.item.datasource.name) });
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }

  // Flow Tasks specific logic

  export function forRunFlowTasks(runFlowTaskInfos: Set<RunFlowTaskInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (runFlowTaskInfos.size() === 1) {
      const task = runFlowTaskInfos.first();
      descriptionTranslateKey = 'changeScheduleRunFlowTaskPrompt';
      descriptionTranslateValues = { FLOW_NAME: L10n.elideIfLong(task.flow.name) };
    } else {
      descriptionTranslateKey = 'changeScheduleRunFlowTasksPrompt';
      descriptionTranslateValues = { COUNT: runFlowTaskInfos.size() };
    }

    const options: IChangeAdminScheduleActionOptions<RunFlowTaskInfo> = {
      action: (ids, scheduleId) => RunFlowTasks.setSchedule(ids, scheduleId),
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeScheduleRunFlowTasksSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleRunFlowTasksError),
      },
      errorDetailFn: (schedule) => runFlowTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Flow,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return execute(runFlowTaskInfos, context, options);
  }

  function runFlowTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<RunFlowTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.flow;
      return {
        itemName: namedItem.name,
        errorMessage: runFlowTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function runFlowTaskErrorMessageFor(errorItem: IErrorItem<RunFlowTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        return Messages.runFlowTaskNotFoundForFlow({ FLOW_NAME: L10n.elideIfLong(errorItem.item.flow.name) });
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }

  export function forDataAccelerationTasks(accelerationTaskInfos: Set<IAccelerationTaskInfo>, context: IActionContext): ng.IPromise<IResult> {
    let descriptionTranslateKey: string;
    let descriptionTranslateValues: _.Dictionary<any>;

    if (accelerationTaskInfos.size() === 1) {
      const task = accelerationTaskInfos.first();
      descriptionTranslateKey = 'changeScheduleDataAccelerationTaskWorkbookPrompt';
      descriptionTranslateValues = { WORKBOOK_NAME: L10n.elideIfLong(task.workbook.name) };
    } else {
      descriptionTranslateKey = 'changeScheduleDataAccelerationTasksPrompt';
      descriptionTranslateValues = { COUNT: accelerationTaskInfos.size() };
    }

    const options: IChangeAdminScheduleActionOptions<IAccelerationTaskInfo> = {
      action: (ids, scheduleId) => AccelerationTasks.setSchedule(ids, scheduleId),
      notificationTranslationFns: {
        success: standardActionSuccessMsg(Messages.changeScheduleDataAccelerationTasksSuccess),
        error: standardActionErrorMsg(Messages.changeScheduleDataAccelerationTasksError),
      },
      errorDetailFn: (schedule) => dataAccelerationTaskErrorDetailFactory(schedule),
      scheduledActionType: ScheduleActions.Data_Acceleration,
      descriptionTranslateKey: descriptionTranslateKey,
      descriptionTranslateValues: descriptionTranslateValues
    };

    return execute(accelerationTaskInfos, context, options);
  }

  function dataAccelerationTaskErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IAccelerationTaskInfo>): IErrorDetail => {
      const namedItem: INamedItemInfo = errorItem.item.workbook;
      return {
        itemName: namedItem.name,
        errorMessage: dataAccelerationTaskErrorMessageFor(errorItem, schedule)
      };
    };
  }

  function dataAccelerationTaskErrorMessageFor(errorItem: IErrorItem<IAccelerationTaskInfo>, schedule: ISchedule): string {
    switch (errorItem.error.code) {
      case SchedulingErrorCode.SchedulingTaskNotFound:
        return Messages.dataAccelerationTaskNotFoundForWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(errorItem.item.workbook.name) });
      case DataAccelerationErrorCode.OBJECT_NOT_ENABLED:
        return Messages.dataAccelerationObjectNotEnabled({ WORKBOOK_NAME: L10n.elideIfLong(errorItem.item.workbook.name) });
      case DataAccelerationErrorCode.LIVE_DATASOURCE_NOT_SUPPORTED:
        return Messages.dataAccelerationLiveDatasourceNotSupported();
      case DataAccelerationErrorCode.TASKS_NOT_ALLOWED_FOR_EXTRACTS_ONLY_WORKBOOK:
        return Messages.dataAccelerationTaskNotAllowedForExtractsOnlyWorkbook({ WORKBOOK_NAME: L10n.elideIfLong(errorItem.item.workbook.name) });
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.dataAccelerationCallingUserInsufficientPermissions();
    }
    return setScheduleErrorMessageFor(errorItem, schedule);
  }

}